import React from 'react'
import { useTranslation } from 'react-i18next'
import { View, StyleSheet, Text, StyleProp, ViewStyle } from 'react-native'
import Button from '../Button'
import { useFormik } from 'formik'
import { PasswordErrorText } from '@linqpal/components/src/ui/ErrorTextComponent'
import { validatePassword } from '../../helpers/validationHelpers'
import { PasswordInput } from '../../../../../ui/atoms/PasswordInput'

import { Spacer } from '@linqpal/components/src/ui'
import { IPasswordError } from './types'
import useIsMobile from '../../../../Contractor/PayablesTab/hooks/useIsMobile'

type Props = {
  onPress: (values: { password: string }) => void | Promise<void>
  containerStyle?: StyleProp<ViewStyle>
}

const CreatePassword: React.FC<Props> = ({ onPress, containerStyle }) => {
  const { t } = useTranslation('global')
  const isMobile = useIsMobile()

  const {
    values,
    handleChange,
    handleSubmit,
    isSubmitting,
    isValid,
    setFieldTouched,
    setFieldError,
    handleBlur,
    errors,
    touched,
    setSubmitting,
  } = useFormik({
    initialValues: {
      password: '',
    },
    validate: (newValues) => {
      const passwordValidation = validatePassword(newValues.password)
      if (passwordValidation) {
        const validationErrors: IPasswordError = passwordValidation
        return validationErrors
      }

      return {}
    },
    onSubmit: async ({ password }) => {
      if (!password?.length) {
        setFieldError(
          'password',
          t('integrations.pay-as-guest.enter-password.password-hint'),
        )
        setFieldTouched('password', true)
        setSubmitting(false)
        return
      }
      try {
        await onPress({ password })

        setSubmitting(false)
      } catch (error) {
        setSubmitting(false)
      }
    },
  })

  return (
    <View style={[styles.container, containerStyle]}>
      <Text
        style={isMobile ? [styles.title, styles.mobile] : styles.title}
        testID={'invoice-processed-main-text'}
      >
        {t('integrations.pay-as-guest.enter-password.title')}
      </Text>
      <Spacer height={3} />

      <View style={styles.passwordInput}>
        <PasswordInput
          value={values.password}
          height={'48px'}
          mandatory
          onChange={handleChange('password')}
          label={t('integrations.pay-as-guest.enter-password.enter-safe')}
          onBlur={() => {
            setFieldTouched('password', true)
            handleBlur('password')
          }}
          validationError={
            !!(Object.keys(errors).length > 0 && touched.password)
          }
          hint={
            Object.keys(errors).length > 0 && touched.password ? (
              <PasswordErrorText
                containerStyle={styles.error}
                passwordValidation={errors}
              />
            ) : (
              <View style={styles.hintContainer}>
                <Text style={styles.hint}>
                  {t(
                    'integrations.pay-as-guest.enter-password.password-hint-correct',
                  )}
                </Text>
              </View>
            )
          }
        />
      </View>
      <Spacer height={40} />

      <Button
        label={t('integrations.pay-as-guest.enter-password.create-account')}
        onPress={handleSubmit}
        buttonStyle={styles.button}
        disabled={isSubmitting || !isValid}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 69.5,
    paddingVertical: 28,
    borderRadius: 20,
    maxWidth: 700,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  title: {
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 24,
    lineHeight: 32,
    color: '#19262F',
    textAlign: 'center',
    width: '80%',
  },
  mobile: {
    width: '100%',
  },
  passwordInput: {
    marginTop: 30,
    width: '100%',
  },
  button: {
    width: '100%',
  },
  hintContainer: {
    marginTop: 10,
  },
  hint: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 14.4,
    color: '#001929',
  },
  error: {
    marginTop: 14,
  },
})

export default CreatePassword
